import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { useLocalStorage } from './useLocalStorage'
import { supabase, checkSubscription, ensureProfile, signOutUser } from '../lib/supabase'
import { setBillingAccount } from '../lib/nativeBilling'
import { programAt } from '../config/program'
import { disableCarePush } from '../lib/carePush'
import { signInError } from '../lib/signInFlow'
import { usePractice } from './usePractice'

const AppContext = createContext(null)

// A session counts as "in progress" once past this fraction and before the end,
// so a stray two-second tap does not populate "Continue listening".
const RESUME_MIN = 0.02
const RESUME_MAX = 0.97

export function AppProvider({ children }) {
  const [userEmail, setUserEmail] = useLocalStorage('regulated_email', null)
  const [completedSessions, setCompletedSessions] = useLocalStorage('regulated_completed', [])
  const [progress, setProgress] = useLocalStorage('regulated_progress', {})
  const [isPremium, setIsPremium] = useState(false)
  const [onboardingDone, setOnboardingDone] = useLocalStorage('regulated_onboarding', false)
  // 'program' | 'browse'. Browse is the default and stays the default while
  // the program is unapproved, brief phase 4.
  const [mode, setMode] = useLocalStorage('regulated_mode', 'browse')
  // How many program days are finished. See src/config/program.js for why this
  // is a counter rather than a set of session ids.
  const [programDay, setProgramDay] = useLocalStorage('regulated_program_day', 0)
  const [toasts, setToasts] = useState([])
  const [authUser, setAuthUser] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [authError, setAuthError] = useState('')
  const practice = usePractice(authReady ? authUser?.id || 'guest' : null)

  const accountId = useRef(null)
  const premiumRevision = useRef(0)
  const authRevision = useRef(0)
  const providerLive = useRef(false)

  const refreshPremium = useCallback(async () => {
    const expectedAccountId = accountId.current
    const revision = ++premiumRevision.current
    const current = () => providerLive.current &&
      revision === premiumRevision.current && accountId.current === expectedAccountId
    try {
      const active = expectedAccountId ? await checkSubscription(expectedAccountId) : false
      if (!current()) return null
      setIsPremium(active)
      return active
    } catch (error) {
      if (!current()) return null
      setIsPremium(false)
      throw error
    }
  }, [])

  useEffect(() => {
    let live = true
    const initialRevision = authRevision.current
    providerLive.current = true

    function adopt(session) {
      if (!live) return
      const user = session?.user ?? null
      premiumRevision.current++
      authRevision.current++
      if (accountId.current !== (user?.id ?? null)) setIsPremium(false)
      accountId.current = user?.id ?? null
      void setBillingAccount(accountId.current).catch(() => {})
      setAuthUser(user ? { ...user } : null)
      setAuthReady(true)
      if (user) setAuthError('')
      if (user?.email) {
        setUserEmail(user.email)
        ensureProfile(user)
      }
    }

    supabase.auth.initialize()
      .then(async ({ error }) => {
        const { data } = await supabase.auth.getSession()
        if (initialRevision !== authRevision.current) return
        adopt(data.session)
        if (live && !data.session && error) setAuthError(signInError(error, true))
      })
      .catch(() => {
        if (initialRevision !== authRevision.current) return
        adopt(null)
        if (live) setAuthError(signInError(null, true))
      })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => adopt(session))

    return () => {
      live = false
      providerLive.current = false
      void setBillingAccount(null).catch(() => {})
      premiumRevision.current++
      sub.subscription.unsubscribe()
    }
    // setUserEmail changes each render; the auth listener must stay mounted.
  }, [])

  useEffect(() => {
    refreshPremium().catch(() => console.error('[useApp] subscription check failed'))
  }, [authUser, refreshPremium])

  async function signOut() {
    authRevision.current++
    premiumRevision.current++
    accountId.current = null
    void setBillingAccount(null).catch(() => {})
    setAuthUser(null)
    setUserEmail(null)
    setIsPremium(false)
    await disableCarePush().catch(() => {})
    await signOutUser()
  }

  function markSessionComplete(sessionId) {
    if (!completedSessions.includes(sessionId)) {
      setCompletedSessions([...completedSessions, sessionId])
    }
    // Listening qualification owns practice credit; completion owns progress only.
    // The program only moves when the session just finished is the one it was
    // waiting on, so listening ahead in Browse never skips a day.
    const { today } = programAt(programDay)
    if (today && String(today.session_id) === String(sessionId)) setProgramDay(programDay + 1)

    // Finished sessions leave "Continue listening".
    setProgress(prev => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })
  }

  // Position in seconds. Drives the in-progress row state and the design's
  // "Continue listening" card. Local only, nothing is written to Supabase.
  function saveProgress(sessionId, position, duration) {
    if (!sessionId || !duration || !Number.isFinite(position)) return
    const ratio = position / duration
    setProgress(prev => {
      const next = { ...prev }
      if (ratio < RESUME_MIN || ratio > RESUME_MAX) delete next[sessionId]
      else next[sessionId] = { position, duration, updatedAt: Date.now() }
      return next
    })
  }

  // Most recently touched unfinished session, or null.
  function lastInProgress(sessions) {
    const entries = Object.entries(progress)
      .filter(([id]) => sessions.some(s => String(s.id) === String(id)))
      .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
    if (!entries.length) return null
    const [id, p] = entries[0]
    return { session: sessions.find(s => String(s.id) === String(id)), ...p }
  }

  function addToast(message, type = 'info', duration = 4000) {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, duration)
  }

  return (
    <AppContext.Provider value={{
      userEmail, setUserEmail,
      completedSessions, markSessionComplete,
      progress, saveProgress, lastInProgress,
      isPremium, refreshPremium,
      authUser, authReady, authError, signOut,
      onboardingDone, setOnboardingDone,
      mode, setMode,
      programDay,
      ...practice,
      toasts, addToast,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}
